import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import { Clock, LogIn, LogOut, Calendar, ListChecks, TrendingUp, FileText, Utensils, Wallet } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import PageHeader from '@/components/PageHeader';
import { Card, StatCard } from '@/components/Card';
import { StatusBadge } from '@/components/Badge';
import Loader from '@/components/Loader';
import LiveWorkingHours from '@/components/LiveWorkingHours';
import ConfirmDialog from '@/components/ConfirmDialog';
import { attendanceService, taskService, leaveService } from '@/services';
import { fmtTime, isLunchOver } from '@/utils/format';

export default function EmployeeDashboard() {
  const qc = useQueryClient();
  const [confirmState, setConfirmState] = useState(null);
  const today = useQuery({ queryKey: ['attendance', 'today'], queryFn: attendanceService.today });
  const summary = useQuery({
    queryKey: ['attendance', 'summary', dayjs().format('YYYY-MM')],
    queryFn: () => attendanceService.mySummary({ year: dayjs().year(), month: dayjs().month() + 1 }),
  });
  const myTasks = useQuery({
    queryKey: ['tasks', 'mine'],
    queryFn: () => taskService.list({ mine: true, limit: 5 }),
  });
  const myLeaves = useQuery({
    queryKey: ['leaves', 'mine', 'recent'],
    queryFn: () => leaveService.mine({ limit: 5 }),
  });
  const leaveBalance = useQuery({
    queryKey: ['leaves', 'balance'],
    queryFn: () => leaveService.balance(),
  });

  const checkIn = useMutation({
    mutationFn: () => attendanceService.checkIn(),
    onSuccess: () => {
      toast.success('Checked in');
      qc.invalidateQueries({ queryKey: ['attendance'] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed'),
  });
  const checkOut = useMutation({
    mutationFn: () => attendanceService.checkOut(),
    onSuccess: () => {
      toast.success('Checked out');
      qc.invalidateQueries({ queryKey: ['attendance'] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed'),
  });
  const startLunch = useMutation({
    mutationFn: () => attendanceService.startLunch(),
    onSuccess: () => {
      toast.success('Lunch break started');
      qc.invalidateQueries({ queryKey: ['attendance'] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed'),
  });
  const endLunch = useMutation({
    mutationFn: () => attendanceService.endLunch(),
    onSuccess: () => {
      toast.success('Lunch break ended');
      qc.invalidateQueries({ queryKey: ['attendance'] });
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed'),
  });

  const att = today.data?.attendance;
  const sum = summary.data?.summary;
  const onLunch = !!att?.lunchStartAt && !att?.lunchEndAt;
  const lunchDone = !!att?.lunchStartAt && !!att?.lunchEndAt;
  // Currently in an open session (checked in, not yet out). Employees can check
  // in/out multiple times a day, so this drives the buttons — not just checkInAt.
  const isCheckedIn = !!att?.checkInAt && !att?.checkOutAt;
  const sessionCount = att?.sessions?.length || (att?.checkInAt ? 1 : 0);

  // Build weekly chart data from summary.items
  const chartData = (summary.data?.items || []).slice(-7).map((it) => ({
    day: dayjs(it.date).format('DD MMM'),
    hours: +(it.workingMinutes / 60).toFixed(2),
  }));

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" subtitle={dayjs().format('dddd, DD MMMM YYYY')} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 gap-4">
        <Card className="lg:col-span-2 lg:row-span-2 flex flex-col bg-gradient-to-br from-brand-600 to-indigo-700 text-white border-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm/none text-white/80">Today's status</div>
              <div className="mt-2 text-3xl font-bold">
                {att?.checkInAt ? (isCheckedIn ? 'Checked in' : 'Checked out') : 'Not checked in'}
              </div>
              <div className="mt-1 text-white/80 text-sm">
                {att?.checkInAt && `In: ${fmtTime(att.checkInAt)}`}
                {att?.checkOutAt && ` · Out: ${fmtTime(att.checkOutAt)}`}
                {sessionCount > 1 && ` · ${sessionCount} sessions`}
              </div>
              {att?.lunchStartAt && (
                <div className="mt-1 text-white/70 text-xs flex items-center gap-1 flex-wrap">
                  <Utensils size={12} />
                  Lunch: {fmtTime(att.lunchStartAt)}
                  {att.lunchEndAt
                    ? ` – ${fmtTime(att.lunchEndAt)} (${att.lunchMinutes || 0}m)`
                    : ' · ongoing'}
                  {isLunchOver(att.lunchMinutes) && (
                    <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-400 text-amber-950 font-semibold">
                      Over 1h
                    </span>
                  )}
                </div>
              )}
            </div>
            <Clock className="opacity-50" size={36} />
          </div>
          <div className="mt-auto pt-5 flex flex-wrap gap-2">
            <button
              onClick={() => checkIn.mutate()}
              disabled={isCheckedIn}
              className="bg-white text-brand-700 hover:bg-white/90 btn"
            >
              <LogIn size={16} /> {sessionCount > 0 && !isCheckedIn ? 'Check in again' : 'Check in'}
            </button>
            {onLunch ? (
              <button
                onClick={() => endLunch.mutate()}
                className="bg-amber-400 text-amber-950 hover:bg-amber-300 btn"
              >
                <Utensils size={16} /> End lunch
              </button>
            ) : (
              <button
                onClick={() =>
                  setConfirmState({
                    title: 'Start lunch break?',
                    message: 'Your lunch timer starts now. You get one lunch break per day, so start it only when you actually break.',
                    confirmLabel: 'Start lunch',
                    icon: Utensils,
                    onConfirm: () => startLunch.mutateAsync().catch(() => {}),
                  })
                }
                disabled={!att?.checkInAt || !!att?.checkOutAt || lunchDone}
                className="bg-white/10 hover:bg-white/20 text-white btn"
                title={lunchDone ? 'Lunch already taken today' : 'Start your lunch break'}
              >
                <Utensils size={16} /> {lunchDone ? 'Lunch taken' : 'Start lunch'}
              </button>
            )}
            <button
              onClick={() =>
                setConfirmState({
                  title: 'Check out now?',
                  message: 'This ends the current session and pauses the live counter. You can check in again later — your time keeps adding up.',
                  confirmLabel: 'Check out',
                  icon: LogOut,
                  onConfirm: () => checkOut.mutateAsync().catch(() => {}),
                })
              }
              disabled={!att?.checkInAt || !!att?.checkOutAt}
              className="bg-white/10 hover:bg-white/20 text-white btn"
            >
              <LogOut size={16} /> Check out
            </button>
          </div>
        </Card>

        <StatCard
          icon={Clock}
          color="emerald"
          label="Working hours"
          value={<LiveWorkingHours att={att} />}
          hint={att?.checkInAt && !att?.checkOutAt ? 'Counting live · Today' : 'Today'}
        />
        <StatCard
          icon={TrendingUp}
          color="violet"
          label="This month"
          value={`${sum?.totalHours || 0} h`}
          hint={`${sum?.present || 0} present · ${sum?.late || 0} late`}
        />
        <StatCard
          icon={Wallet}
          color={(leaveBalance.data?.balance ?? 0) < 0 ? 'rose' : 'emerald'}
          label="Leave balance"
          value={`${leaveBalance.data?.balance ?? 0} left`}
          hint={`+${leaveBalance.data?.accrualPerMonth ?? 1} credited each month`}
        />
        <StatCard
          icon={Calendar}
          color="sky"
          label="Leaves used"
          value={`${leaveBalance.data?.usedThisYear ?? 0} days`}
          hint="Approved this year"
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="font-semibold">Hours worked — last 7 days</div>
          </div>
          {chartData.length === 0 ? (
            <div className="h-56 grid place-items-center text-sm text-slate-500">No data yet</div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="day" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="hours" fill="#3a64ff" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card>
          <div className="font-semibold mb-3 flex items-center gap-2">
            <ListChecks size={18} /> My open tasks
          </div>
          {myTasks.isLoading && <Loader />}
          {!myTasks.isLoading && (myTasks.data?.items || []).length === 0 && (
            <div className="text-sm text-slate-500 py-4">No tasks assigned</div>
          )}
          <ul className="space-y-2">
            {(myTasks.data?.items || []).map((t) => (
              <li key={t._id} className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{t.title}</div>
                  <div className="text-xs text-slate-500 truncate">{t.project?.name}</div>
                </div>
                <StatusBadge status={t.status} />
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <div className="font-semibold mb-3 flex items-center gap-2">
            <Calendar size={18} /> Recent leaves
          </div>
          {myLeaves.isLoading && <Loader />}
          {!myLeaves.isLoading && (myLeaves.data?.items || []).length === 0 && (
            <div className="text-sm text-slate-500 py-4">No leave requests</div>
          )}
          <ul className="space-y-2">
            {(myLeaves.data?.items || []).map((l) => {
              const isShort = l.type === 'short';
              const sameDay = !isShort && l.endDate && dayjs(l.startDate).isSame(l.endDate, 'day');
              const durationLabel = isShort
                ? `${(l.hours || 0).toFixed(1)}h`
                : `${l.days}d`;
              return (
                <li key={l._id} className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-medium capitalize">
                      {isShort ? 'Short leave' : l.type} · {durationLabel}
                    </div>
                    <div className="text-xs text-slate-500">
                      {dayjs(l.startDate).format('DD MMM')}
                      {!isShort && l.endDate && !sameDay && (
                        <> → {dayjs(l.endDate).format('DD MMM')}</>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={l.status} />
                </li>
              );
            })}
          </ul>
        </Card>

        <Card>
          <div className="font-semibold mb-3 flex items-center gap-2">
            <FileText size={18} /> Quick tips
          </div>
          <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
            <li>• Check in on time to avoid a late mark — your shift settings are on your profile.</li>
            <li>• Log your daily work at the end of the day under "Daily Work".</li>
            <li>• Apply for leaves in advance for faster approval.</li>
          </ul>
        </Card>
      </div>

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}
